import React, { useEffect, useState } from 'react';
import Appearance from 'styles/Appearance.js';
import Button from 'views/Button.js';
import Utils from 'files/Utils.js';
import Request from 'files/Request.js';
import Captcha from 'files/Captcha.js';

const ConsultationModal = ({ open, onClose, utils, sourceType }) => {

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // start loading recaptcha as soon as the form is shown
    useEffect(() => {
        if(open && utils) {
            Captcha.load().catch(() => {});
        }
    }, [open, utils]);

    // Reset the form a moment after the modal closes so the next open is fresh
    useEffect(() => {
        if(open) {
            return;
        }
        let timeout = setTimeout(() => {
            setName('');
            setEmail('');
            setPhone('');
            setAddress('');
            setError(null);
            setSubmitted(false);
        }, 350);
        return () => clearTimeout(timeout);
    }, [open]);

    // Close on Escape
    useEffect(() => {
        if(!open) {
            return;
        }
        const onKeyDown = (e) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    const handleSubmit = async () => {
        if(!name.trim()) {
            setError('Please enter your name.');
            return;
        }
        if(!Utils.validateEmail(email)) {
            setError('Please enter a valid email address.');
            return;
        }
        if(phone.replace(/[^0-9]/g, '').length < 10) {
            setError('Please enter a valid phone number.');
            return;
        }
        if(!address.trim()) {
            setError('Please enter your home address.');
            return;
        }
        setError(null);
        setSubmitting(true);
        try {
            if(utils) {
                await Request.post(utils, '/shop/', {
                    type: sourceType || 'dealer_consultation',
                    name, email, phone, address,
                    captcha_token: await Captcha.getToken('dealer_consultation'),
                });
            } else {
                // No server context wired — keep working in isolation (e.g. previews)
                console.log('Consultation request:', { name, email, phone, address });
            }
            setSubmitted(true);
        } catch(err) {
            setError((err && err.message) || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const fieldStyle = {
        ...Appearance.styles.input(),
        borderRadius: 12,
    };

    return (
        <>
            {/* Overlay */}
            <div
                className={`overlay ${open ? 'active' : ''}`}
                onClick={onClose} />

            {/* Modal */}
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 20,
                pointerEvents: open ? 'all' : 'none',
            }}>
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        width: '100%',
                        maxWidth: 480,
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        backgroundColor: Appearance.colors.white,
                        borderRadius: 24,
                        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                        opacity: open ? 1 : 0,
                        transform: open ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.98)',
                        transition: 'opacity 0.3s ease, transform 0.3s ease',
                        position: 'relative',
                    }}>

                    {/* Close button */}
                    <div
                        onClick={onClose}
                        style={{
                            position: 'absolute',
                            top: 20,
                            right: 20,
                            width: 36,
                            height: 36,
                            borderRadius: 18,
                            backgroundColor: Appearance.colors.offWhite,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            zIndex: 1,
                        }}>
                        <i className={'fas fa-times'} style={{ fontSize: 14, color: Appearance.colors.darkGrey }} />
                    </div>

                    <div style={{ padding: Utils.isMobile() ? '36px 24px' : '44px 40px' }}>

                        {submitted ? (
                            /* Success state */
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                gap: 16,
                                padding: '20px 0',
                            }}>
                                <div style={{
                                    width: 64,
                                    height: 64,
                                    borderRadius: 32,
                                    backgroundColor: 'rgba(76,175,80,0.15)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                    <i className={'fas fa-check'} style={{ fontSize: 26, color: Appearance.colors.success }} />
                                </div>
                                <h3 style={Appearance.textStyles.heading()}>
                                    {'Request Received'}
                                </h3>
                                <p style={Appearance.textStyles.body({ textAlign: 'center' })}>
                                    {`Thanks, ${name.trim().split(' ')[0]}! An authorized Aqua Link dealer will reach out within one business day to talk through your coverage options and the best way to get you protected.`}
                                </p>
                                <Button
                                    label={'Done'}
                                    type={'primary'}
                                    onClick={onClose}
                                    style={{ width: '100%', marginTop: 8 }} />
                            </div>
                        ) : (
                            <>
                                {/* Header */}
                                <span style={{
                                    ...Appearance.textStyles.label(),
                                    display: 'block',
                                    marginBottom: 10,
                                }}>
                                    {'Authorized Local Dealer'}
                                </span>
                                <h3 style={{
                                    ...Appearance.textStyles.heading(),
                                    marginBottom: 10,
                                }}>
                                    {'Let’s plan your coverage'}
                                </h3>
                                <p style={{
                                    ...Appearance.textStyles.body({ fontSize: 14 }),
                                    marginBottom: 28,
                                }}>
                                    {'Aqua Link works with authorized dealers in your area. Share a few details and a local dealer will reach out to talk through the coverage that’s right for your home — no obligation.'}
                                </p>

                                {/* Form */}
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 14,
                                }}>
                                    <div>
                                        <label style={fieldLabelStyle}>{'Full Name'}</label>
                                        <input
                                            type={'text'}
                                            placeholder={'Jane Doe'}
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            style={fieldStyle} />
                                    </div>
                                    <div>
                                        <label style={fieldLabelStyle}>{'Email Address'}</label>
                                        <input
                                            type={'email'}
                                            placeholder={'jane@example.com'}
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            style={fieldStyle} />
                                    </div>
                                    <div>
                                        <label style={fieldLabelStyle}>{'Phone Number'}</label>
                                        <input
                                            type={'tel'}
                                            placeholder={'(555) 123-4567'}
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            style={fieldStyle} />
                                    </div>
                                    <div>
                                        <label style={fieldLabelStyle}>{'Home Address'}</label>
                                        <textarea
                                            placeholder={'123 Main St, Apt 4, City, State ZIP'}
                                            value={address}
                                            onChange={(e) => setAddress(e.target.value)}
                                            rows={2}
                                            style={{
                                                ...fieldStyle,
                                                resize: 'vertical',
                                                minHeight: 56,
                                                lineHeight: 1.5,
                                            }} />
                                    </div>
                                </div>

                                {error && (
                                    <span style={{
                                        ...Appearance.textStyles.caption({ color: Appearance.colors.error }),
                                        display: 'block',
                                        marginTop: 14,
                                    }}>
                                        {error}
                                    </span>
                                )}

                                <Button
                                    label={'Connect me with a local dealer'}
                                    type={'primary'}
                                    icon={'fas fa-handshake'}
                                    loading={submitting}
                                    onClick={handleSubmit}
                                    style={{ width: '100%', marginTop: 24 }} />

                                <p style={{
                                    ...Appearance.textStyles.caption({ textAlign: 'center' }),
                                    marginTop: 16,
                                }}>
                                    {'No obligation. We\'ll never share your information.'}
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

const fieldLabelStyle = {
    ...Appearance.textStyles.body({ fontWeight: 600, color: Appearance.colors.text(), fontSize: 13 }),
    display: 'block',
    marginBottom: 6,
};

export default ConsultationModal;
